import { UseQueryResult, useQuery } from "@tanstack/react-query";
import { ReactNode, createContext, useContext, useState } from "react";
import { APIClient } from "../constants";

type MultiSigWallet = {
  address: string;
  userPublicKey: string;
  checkSequenceVerifyLock: number;
  outputs: [];
};

type WalletContextState = {
  ordinalsPubkey?: string;
  ordinalsAddress?: string;
  ordinalsAddressType?: string;
  paymentPubkey?: string;
  paymentAddress?: string;
  connected: boolean;
  multiSigWallet: UseQueryResult<MultiSigWallet | null, unknown>;

  setOrdinalsPubkey: (pubkey: string) => void;
  setPaymentPubkey: (pubkey: string) => void;
  setOrdinalsAddress: (address: string) => void;
  setPaymentAddress: (address: string) => void;
  setOrdinalsAddressType: (addressType: string) => void;
};

const WalletContext = createContext<WalletContextState>(
  {} as WalletContextState
);

export const useWalletContext = () => {
  return useContext(WalletContext);
};

const useMSWallet = (pubkey: string) => {
  return useQuery(
    ["mswallet", pubkey],
    async (): Promise<MultiSigWallet | null> => {
      try {
        const { data } = await APIClient.get<MultiSigWallet[]>("/mswallets", {
          params: {
            userPublicKey: pubkey,
          },
        });
        return data?.at(-1) ?? null;
      } catch (err) {
        console.error(err);
        return null;
      }
    },
    {
      enabled: !!pubkey,
    }
  );
};

export const WalletProvider = (props: { children: ReactNode }) => {
  const [ordinalsPubkey, setOrdinalsPubkey] = useState<string>();
  const [paymentPubkey, setPaymentPubkey] = useState<string>();
  const [ordinalsAddress, setOrdinalsAddress] = useState<string>();
  const [paymentAddress, setPaymentAddress] = useState<string>();
  const [ordinalsAddressType, setOrdinalsAddressType] = useState<string>();

  const mswalletQuery = useMSWallet(paymentPubkey || "");

  return (
    <WalletContext.Provider
      value={{
        ordinalsPubkey,
        paymentPubkey,
        setOrdinalsPubkey,
        setPaymentPubkey,
        ordinalsAddress,
        paymentAddress,
        setOrdinalsAddress,
        setPaymentAddress,
        ordinalsAddressType,
        setOrdinalsAddressType,
        multiSigWallet: mswalletQuery,
        connected: !!ordinalsPubkey && !!paymentPubkey,
      }}
    >
      {props.children}
    </WalletContext.Provider>
  );
};
